import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { Search, Info, Table2, LayoutGrid, FolderTree, Columns3, X, Save } from "lucide-react";
import { SourceValidation } from "@/components/SourceValidation";
import GovernancePanel from "@/components/SourceGovernance";
import SourceDetail, { VERIF_BG, SCIREV_BG } from "@/components/SourceDetail";
import AddSourceWizard from "@/components/AddSourceWizard";

const ACCESS_BG = {
  "Publicly available": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Licensed or subscription-based": "bg-indigo-50 text-indigo-700 border-indigo-200",
  "Client-confidential": "bg-amber-50 text-amber-700 border-amber-200",
  "Commissioned research": "bg-teal-50 text-teal-700 border-teal-200",
  "Internal MarksMan material": "bg-purple-50 text-purple-700 border-purple-200",
  "Restricted—metadata only": "bg-red-50 text-red-700 border-red-200",
};

const ALL_COLUMNS = [
  ["source_type", "Type"], ["provenance", "Provenance"], ["date", "Date"], ["geography", "Geography"],
  ["access_classification", "Access"], ["cutoff_status", "Cut-off"], ["verification_status", "Verification"],
  ["sci_review_status", "Scientific review"], ["evidence_quality", "Quality"], ["statements", "Statements"],
  ["insights", "Insights"], ["reviewer", "Reviewer"], ["date_added", "Added"],
];
const DEFAULT_COLS = ["source_type", "date", "access_classification", "cutoff_status", "verification_status", "sci_review_status", "evidence_quality", "insights"];

const FILTER_DEFS = [
  ["source_type", "Source type", "source_types"],
  ["provenance", "Provenance", "provenances"],
  ["access_classification", "Access class", "access_classifications"],
  ["verification_status", "Verification", "verification_statuses"],
  ["sci_review_status", "Scientific review", "sci_review_statuses"],
  ["evidence_quality", "Evidence quality", "quality_levels"],
  ["cutoff_status", "Cut-off eligibility", "cutoff_classes"],
];

const METRICS = [
  ["total", "Total sources", null, "text-slate-900"],
  ["eligible", "Eligible within cut-off", { cutoff_status: "Eligible — on or before cut-off" }, "text-teal-700"],
  ["verification_pending", "Verification pending", { verification_status: "Verification pending" }, "text-amber-600"],
  ["source_verified", "Source verified", { verification_status: "Source verified" }, "text-teal-700"],
  ["under_review", "Under scientific review", { sci_review_status: "Under scientific review" }, "text-sky-700"],
  ["approved", "Approved for synthesis", { sci_review_status: "Approved for use in synthesis" }, "text-teal-700"],
  ["excluded", "Excluded from synthesis", { sci_review_status: "Excluded from synthesis" }, "text-red-600"],
  ["duplicates", "In evidence families", { _family: "multi" }, "text-indigo-700"],
  ["post_cutoff", "Post-cut-off", { cutoff_status: "Post-cut-off — excluded from current assessment" }, "text-amber-600"],
  ["restricted", "Access-restricted", { _restricted: "yes" }, "text-purple-700"],
];

const RESTRICTED = ["Client-confidential", "Commissioned research", "Internal MarksMan material", "Restricted—metadata only", "Licensed or subscription-based"];

function CompactCard({ s, onOpen }) {
  return (
    <div className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-sm" data-testid={`source-card-${s.id}`}>
      <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
        <span className="font-mono text-[10px] text-slate-400 bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5">{s.id}</span>
        <Badge variant="outline" className="text-[9px] bg-indigo-50 text-indigo-700 border-indigo-200">{s.source_type}</Badge>
        {s.archived && <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-700 border-amber-300" data-testid={`archived-badge-${s.id}`}>Archived</Badge>}
        <span className="text-[9px] font-mono uppercase text-amber-600/80 ml-auto">Illustrative demonstration data</span>
      </div>
      <h4 className="text-[13px] font-bold text-slate-900 leading-snug">{s.title}</h4>
      <div className="flex flex-wrap gap-1.5 mt-2">
        <Badge variant="outline" className={`text-[9px] ${ACCESS_BG[s.access_classification] || "bg-slate-50 text-slate-500 border-slate-200"}`}>{s.access_classification}</Badge>
        <Badge variant="outline" className={`text-[9px] ${VERIF_BG[s.verification_status]}`}>{s.verification_status}</Badge>
        <Badge variant="outline" className={`text-[9px] ${SCIREV_BG[s.sci_review_status]}`}>{s.sci_review_status}</Badge>
      </div>
      <p className="text-[10.5px] text-slate-500 mt-2">
        {s.date || "Date unclear"} · {s.geography || "—"} · Quality: {s.evidence_quality} · {(s.extracted_statements || []).length + (s.hcp_statements || []).length} statements · {(s.linked_insights || []).length} linked insights
      </p>
      <button onClick={() => onOpen(s)} className="text-[11px] font-semibold text-teal-700 hover:text-teal-900 mt-2" data-testid={`open-detail-${s.id}`}>Open source detail →</button>
    </div>
  );
}

export default function Sources({ role, onOpenInsight }) {
  const [lib, setLib] = useState(null);
  const [gov, setGov] = useState(null);
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState("date_added");
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("src_view") || (role?.id === "leadership" ? "cards" : "table"));
  const [cols, setCols] = useState(() => JSON.parse(localStorage.getItem("src_cols") || "null") || DEFAULT_COLS);
  const [detail, setDetail] = useState(null);
  const [savedSets, setSavedSets] = useState(() => JSON.parse(localStorage.getItem("src_saved_filters") || "[]"));
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    api.get(`/source-library${showArchived ? "?include_archived=true" : ""}`).then((r) => setLib(r.data));
    api.get("/sources").then((r) => setGov(r.data));
  }, [showArchived]);

  const setView = (v) => { setViewMode(v); localStorage.setItem("src_view", v); };
  const toggleCol = (k) => setCols((c) => {
    const next = c.includes(k) ? c.filter((x) => x !== k) : [...c, k];
    localStorage.setItem("src_cols", JSON.stringify(next));
    return next;
  });

  const filtered = useMemo(() => {
    if (!lib) return [];
    const needle = q.trim().toLowerCase();
    let rows = lib.sources.filter((s) => {
      for (const [k, v] of Object.entries(filters)) {
        if (!v) continue;
        if (k === "_family") { if (((s.evidence_family || {}).members || []).length <= 1) return false; continue; }
        if (k === "_restricted") { if (!RESTRICTED.includes(s.access_classification)) return false; continue; }
        if (s[k] !== v) return false;
      }
      if (!needle) return true;
      const hay = [s.id, s.title, s.author_speaker, s.organisation, s.conference_journal, s.geography,
        s.evidence_summary, s.quality_rationale, s.reviewer, ...(s.themes || []), ...(s.assets || []), ...(s.competitors || []),
        ...(s.extracted_statements || []).map((st) => st.text), ...(s.claims || []).map((c) => c.text),
        ...(s.hcp_statements || []).map((st) => st.text)].join(" ").toLowerCase();
      return hay.includes(needle);
    });
    if (sort === "date_added") rows = [...rows].sort((a, b) => (b.date_added || "").localeCompare(a.date_added || ""));
    if (sort === "date") rows = [...rows].sort((a, b) => (b.date || "").localeCompare(a.date || ""));
    if (sort === "title") rows = [...rows].sort((a, b) => a.title.localeCompare(b.title));
    if (sort === "id") rows = [...rows].sort((a, b) => a.id.localeCompare(b.id));
    return rows;
  }, [lib, q, filters, sort]);

  const families = useMemo(() => {
    const map = {};
    filtered.forEach((s) => {
      const name = s.evidence_family?.name || "Unassigned";
      (map[name] = map[name] || []).push(s);
    });
    return Object.entries(map).sort((a, b) => b[1].length - a[1].length);
  }, [filtered]);

  if (!lib) return <div className="p-8 text-slate-400 text-sm">Loading source library…</div>;

  const updateSource = (updated) => {
    setLib((prev) => ({ ...prev, sources: prev.sources.map((s) => (s.id === updated.id ? updated : s)) }));
    setDetail(updated);
  };
  const activeFilters = Object.entries(filters).filter(([, v]) => v);
  const applyMetric = (f) => setFilters(f ? { ...f } : {});
  const saveFilterSet = () => {
    const name = window.prompt("Name this filter set:");
    if (!name) return;
    const next = [...savedSets.filter((x) => x.name !== name), { name, filters, q }];
    setSavedSets(next);
    localStorage.setItem("src_saved_filters", JSON.stringify(next));
    toast.success(`Filter set "${name}" saved`);
  };

  const cellVal = (s, k) => {
    if (k === "statements") return (s.extracted_statements || []).length + (s.hcp_statements || []).length;
    if (k === "insights") return (s.linked_insights || []).length;
    if (k === "verification_status") return <Badge variant="outline" className={`text-[9px] ${VERIF_BG[s.verification_status]}`}>{s.verification_status}</Badge>;
    if (k === "sci_review_status") return <Badge variant="outline" className={`text-[9px] ${SCIREV_BG[s.sci_review_status]}`}>{s.sci_review_status}</Badge>;
    if (k === "access_classification") return <Badge variant="outline" className={`text-[9px] ${ACCESS_BG[s.access_classification] || ""}`}>{s.access_classification}</Badge>;
    if (k === "cutoff_status") return <span className={s.cutoff_status?.startsWith("Eligible") ? "text-teal-700" : "text-amber-700"}>{s.cutoff_status?.split(" — ")[0]}</span>;
    if (k === "evidence_quality") return s.evidence_quality?.startsWith("Not applicable") ? "N/A — non-evidence" : s.evidence_quality;
    return s[k] || "—";
  };

  return (
    <div className="space-y-5" data-testid="sources-view">
      <div className="bg-slate-900 rounded-xl p-5 border border-slate-800">
        <h3 className="font-heading text-base font-bold text-white">Governed evidence repository</h3>
        <p className="text-sm text-slate-400 mt-1">The governed evidence repository supporting every HCP statement, scientific finding and strategic interpretation.</p>
        <p className="text-[11px] text-slate-500 mt-2 flex items-start gap-1.5"><Info className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
          Source inclusion does not imply scientific endorsement. Access rights, source verification, evidence quality and scientific review are assessed separately. Evidence cut-off: {lib.evidence_cutoff}. Statuses simulate the intended workflow on illustrative demonstration data.</p>
      </div>

      <Tabs defaultValue="repository">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="repository" className="text-xs" data-testid="tab-repository">Source Repository</TabsTrigger>
          <TabsTrigger value="governance" className="text-xs" data-testid="tab-governance">Governance & Classifications</TabsTrigger>
        </TabsList>

        <TabsContent value="repository" className="space-y-4 mt-4">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="source-metrics">
            {METRICS.map(([key, label, filter, cls]) => (
              <button key={key} onClick={() => applyMetric(filter)} data-testid={`metric-${key}`}
                className="bg-white border border-slate-200/80 rounded-xl p-2.5 shadow-sm text-left hover:border-teal-400 transition-colors">
                <p className="font-mono text-[8.5px] uppercase tracking-widest text-slate-400 leading-tight">{label}</p>
                <p className={`font-heading text-lg font-bold ${cls}`}>{lib.metrics[key]}</p>
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <Input placeholder="Search titles, authors, claims, statements, reviewer comments, themes…" value={q} onChange={(e) => setQ(e.target.value)}
                className="h-8 pl-8 text-xs" data-testid="source-search-input" />
            </div>
            {FILTER_DEFS.map(([k, label, optKey]) => (
              <Select key={k} value={filters[k] || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, [k]: v === "all" ? undefined : v }))}>
                <SelectTrigger className="h-8 w-auto min-w-[120px] text-[11px]" data-testid={`filter-${k}`}><SelectValue placeholder={label} /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="all" className="text-xs">All — {label}</SelectItem>
                  {(lib[optKey] || []).map((o) => <SelectItem key={o} value={o} className="text-xs">{o}</SelectItem>)}
                </SelectContent>
              </Select>
            ))}
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="h-8 w-36 text-[11px]" data-testid="source-sort"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="date_added" className="text-xs">Newest added</SelectItem>
                <SelectItem value="date" className="text-xs">Publication date</SelectItem>
                <SelectItem value="title" className="text-xs">Title A–Z</SelectItem>
                <SelectItem value="id" className="text-xs">Source ID</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" className="h-8 text-[11px]" onClick={saveFilterSet} data-testid="save-filter-set"><Save className="w-3 h-3 mr-1" />Save view</Button>
            {savedSets.length > 0 && (
              <Select onValueChange={(name) => { const set = savedSets.find((x) => x.name === name); if (set) { setFilters(set.filters); setQ(set.q || ""); } }}>
                <SelectTrigger className="h-8 w-32 text-[11px]" data-testid="saved-filter-sets"><SelectValue placeholder="Saved views" /></SelectTrigger>
                <SelectContent>{savedSets.map((x) => <SelectItem key={x.name} value={x.name} className="text-xs">{x.name}</SelectItem>)}</SelectContent>
              </Select>
            )}
            <AddSourceWizard meta={lib} onAdded={(s) => setLib((prev) => ({ ...prev, sources: [s, ...prev.sources], metrics: { ...prev.metrics, total: prev.metrics.total + 1 } }))} />
          </div>

          <SourceValidation onReclassified={() => api.get("/source-library").then((r) => setLib(r.data))} />

          {activeFilters.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5" data-testid="active-filter-chips">
              {activeFilters.map(([k, v]) => (
                <span key={k} className="flex items-center gap-1 text-[10px] bg-teal-50 border border-teal-200 text-teal-700 rounded-full px-2 py-0.5">
                  {k === "_family" ? "In evidence families" : k === "_restricted" ? "Access-restricted" : v}
                  <button onClick={() => setFilters((f) => ({ ...f, [k]: undefined }))} aria-label={`Remove filter ${k === "_family" ? "In evidence families" : k === "_restricted" ? "Access-restricted" : v}`}><X className="w-3 h-3" aria-hidden="true" /></button>
                </span>
              ))}
              <button onClick={() => setFilters({})} className="text-[10.5px] font-semibold text-slate-500 hover:text-slate-800" data-testid="clear-all-filters">Clear all</button>
            </div>
          )}

          <div className="flex items-center justify-between gap-3">
            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-400" data-testid="source-result-count">{filtered.length} of {lib.sources.length} sources</p>
            <button onClick={() => setShowArchived((v) => !v)} data-testid="toggle-archived-btn" aria-pressed={showArchived}
              className={`text-[10px] font-semibold rounded-full px-2.5 py-1 border transition-colors ${showArchived ? "bg-amber-100 text-amber-800 border-amber-300" : "bg-white text-slate-500 border-slate-200 hover:border-amber-300"}`}>
              {showArchived ? "Hide" : "Show"} archived ({lib.metrics?.archived ?? lib.archived_sources?.length ?? 0})
            </button>
            <div className="flex items-center gap-1">
              {[["table", Table2, "Compact table"], ["cards", LayoutGrid, "Summary cards"], ["families", FolderTree, "Evidence families"]].map(([v, Icon, label]) => (
                <button key={v} onClick={() => setView(v)} title={label} data-testid={`view-${v}`} aria-label={`Switch to ${label} view`} aria-pressed={viewMode === v}
                  className={`flex items-center gap-1 text-[10.5px] font-semibold rounded-md px-2.5 py-1.5 border ${viewMode === v ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-500 border-slate-200 hover:border-teal-400"}`}>
                  <Icon className="w-3.5 h-3.5" />{label}
                </button>
              ))}
              {viewMode === "table" && (
                <Popover>
                  <PopoverTrigger asChild>
                    <button className="flex items-center gap-1 text-[10.5px] font-semibold rounded-md px-2.5 py-1.5 border bg-white text-slate-500 border-slate-200 hover:border-teal-400" data-testid="column-picker">
                      <Columns3 className="w-3.5 h-3.5" />Columns
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-56 p-3" align="end">
                    <p className="font-mono text-[9px] uppercase tracking-wide text-slate-400 mb-2">Visible columns (ID & Title frozen)</p>
                    <div className="space-y-1">
                      {ALL_COLUMNS.map(([k, label]) => (
                        <label key={k} className="flex items-center gap-2 text-[11px] text-slate-600 cursor-pointer">
                          <input type="checkbox" checked={cols.includes(k)} onChange={() => toggleCol(k)} className="accent-teal-600" />{label}
                        </label>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              )}
            </div>
          </div>

          {viewMode === "table" && (
            <div className="bg-white border border-slate-200/80 rounded-xl shadow-sm overflow-x-auto" data-testid="source-table">
              <table className="w-full text-xs">
                <thead><tr className="text-left border-b border-slate-200">
                  <th className="py-2 px-3 font-mono text-[9px] uppercase tracking-widest text-slate-400 sticky left-0 bg-white z-10">Source ID</th>
                  <th className="py-2 px-3 font-mono text-[9px] uppercase tracking-widest text-slate-400 sticky left-[92px] bg-white z-10 min-w-[220px]">Title</th>
                  {ALL_COLUMNS.filter(([k]) => cols.includes(k)).map(([k, label]) => (
                    <th key={k} className="py-2 px-3 font-mono text-[9px] uppercase tracking-widest text-slate-400 whitespace-nowrap">{label}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {filtered.map((s) => (
                    <tr key={s.id} className="border-b border-slate-100 align-top hover:bg-slate-50/60 cursor-pointer" onClick={() => setDetail(s)} data-testid={`row-${s.id}`}>
                      <td className="py-2.5 px-3 font-mono text-[10px] text-slate-500 sticky left-0 bg-white">{s.id}</td>
                      <td className="py-2.5 px-3 font-semibold text-slate-800 sticky left-[92px] bg-white max-w-[300px]">
                        {s.title}
                        {s.archived && <Badge variant="outline" className="ml-1.5 text-[9px] bg-amber-50 text-amber-700 border-amber-300" data-testid={`archived-badge-${s.id}`}>Archived</Badge>}
                      </td>
                      {ALL_COLUMNS.filter(([k]) => cols.includes(k)).map(([k]) => (
                        <td key={k} className="py-2.5 px-3 text-slate-600 max-w-[180px]">{cellVal(s, k)}</td>
                      ))}
                    </tr>
                  ))}
                  {filtered.length === 0 && <tr><td colSpan={cols.length + 2} className="py-8 text-center text-slate-400 text-[11px]" data-testid="no-sources-message">No sources match the current search or filters.</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {viewMode === "cards" && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {filtered.map((s) => <CompactCard key={s.id} s={s} onOpen={setDetail} />)}
              {filtered.length === 0 && <p className="text-sm text-slate-500 text-center py-10 col-span-2" data-testid="no-sources-message">No sources match the current search or filters.</p>}
            </div>
          )}

          {viewMode === "families" && (
            <div className="space-y-3" data-testid="family-view">
              {families.map(([name, members]) => (
                <div key={name} className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-sm">
                  <p className="text-xs font-bold text-slate-900">{name} <span className="text-slate-400 font-normal">— {members.length} record{members.length > 1 ? "s" : ""}</span></p>
                  {members.length > 1 && <p className="text-[10px] text-slate-400 mt-0.5">Multiple reports of the same underlying study — counted once as independent evidence.</p>}
                  <div className="mt-2 space-y-1">
                    {members.map((s) => (
                      <button key={s.id} onClick={() => setDetail(s)} className="w-full flex flex-wrap items-center gap-2 text-left text-[11px] text-slate-600 hover:bg-slate-50 rounded-lg p-1.5" data-testid={`family-member-${s.id}`}>
                        <span className="font-mono text-[9.5px] text-slate-400">{s.id}</span>
                        <span className="font-semibold text-slate-700 flex-1 min-w-0 truncate">{s.title}</span>
                        <span className="text-slate-400">{s.source_type} · {s.date}</span>
                        <Badge variant="outline" className={`text-[8.5px] ${VERIF_BG[s.verification_status]}`}>{s.verification_status}</Badge>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="governance" className="mt-4">
          {gov ? <GovernancePanel data={gov} /> : <div className="p-8 text-slate-400 text-sm">Loading governance…</div>}
        </TabsContent>
      </Tabs>

      {detail && <SourceDetail source={detail} onClose={() => setDetail(null)} onUpdated={updateSource} onOpenInsight={onOpenInsight} />}
    </div>
  );
}
